import { Component, Fragment, Host, Listen, State, Prop, Event, h, EventEmitter, Element } from '@stencil/core';
import clsx from 'clsx';

import { FieldValue } from '../../types/form';
import { TextInput } from './inputs/text';
import { fetchForm, saveResponse } from '../../utils/api';
import { Form, FormSuccess } from '../../types/data';
import { ContentInput } from './inputs/content';
import { TextareaInput } from './inputs/textarea';
import { FormClasses } from '../../types/props';
import { createGetClass } from '../../utils/classes';

import { Fetcher } from '../fetcher/fetcher';
import { Success } from './common/success';
import { Content } from './common/content';

type Field = Form['fields'][number];

@Component({
  tag: 'hr-form',
  styleUrl: 'hr-form.css',
  shadow: false,
})
export class HappyreactForm {
  /**
   * Classes
   */
  @Prop() classes: Readonly<{ [key in keyof FormClasses]: FormClasses[key] }>;

  /**
   * modes
   */
  @Prop() mode: 'flow' | 'modal' = 'flow';

  @Element() host: HTMLElement;

  @State() state: {
    dirty: boolean;
    valid: boolean;
    values: Record<string, FieldValue>;
    errors: Record<string, any>;
  } = {
      valid: false,
      dirty: false,
      values: {},
      errors: {
        _count: 0
      }
    }

  handleSend() {
    this.request.status = 'loading';

    saveResponse({
      token: this.props.token,
      reactionId: this.props.reactionId,
      url: this.props.url,
      values: this.state.values,
      resources: this.props.resources,
      count: this.props.count
    })
      .then((response) => {
        this.request = {
          status: 'success',
          data: response
        }
      })
  }

  handleValidate(value: FieldValue, field: Field, state: typeof this.state) {
    for (const validation of field.validations) {
      switch (validation.type) {
        case 'REQUIRED':
          if (!value) {
            const message = `${field.label} is required`;
            state.errors[field.id] = message;

            throw new Error(message);
          }
        default:
          break;
      }
    }

    this.state = state;
  }

  private _countErrors(state) {
    return Object.keys(state.errors).filter((key) => {
      if (key.includes('_')) {
        return false;
      }

      return !!state.errors[key];
    }).length;
  }

  handleSubmit(event: SubmitEvent) {
    event.preventDefault();

    const state = structuredClone(this.state);

    for (const field of this.response.data.fields) {
      try {
        this.handleValidate(state.values[field.id], field, state);
      } catch (cause) {
        state.errors._count = this._countErrors(state);
      } finally {
        this.state = state;
      }
    }

    if (state.errors._count === 0) {
      this.handleSend();
    }
  }

  handleInput(event: InputEvent, field: Field) {
    //@ts-expect-error
    const value = event.target.value as FieldValue;
    const state = structuredClone(this.state);

    state.dirty = true;
    state.values[field.id] = value;

    try {
      this.handleValidate(value, field, state);
      state.errors[field.id] = null;
    } catch (cause) {
      state.errors[field.id] = cause.message;
    }

    state.errors._count = this._countErrors(state);

    this.state = state;
  }

  @State() response:
    | { status: 'idle'; data: null }
    | { status: 'loading'; data: null }
    | { status: 'success'; data: Form }
    | { status: 'error'; data: null } = {
      status: 'loading',
      data: null
    }

  @State() request:
    | { status: 'idle'; data: null }
    | { status: 'loading'; data: null }
    | { status: 'success'; data: FormSuccess } = {
      status: 'idle',
      data: null
    }

  @State() isVisible = false;

  @State() props: { token: string; reactionId: string; resources: string[]; url?: string; count?: number } = null;

  /**
   * Event fired when user click reaction
   */
  @Event() close: EventEmitter;

  /**
   * onReaction event handler
   * @param reaction 
   */
  onCloseHandler() {
    this.close.emit();

    this.isVisible = false;
  }

  @Listen('show')
  showHandler(event: CustomEvent<typeof this.props>) {
    this.props = event.detail;
    this.state = {
      valid: false,
      dirty: false,
      values: {},
      errors: {
        _count: 0
      }
    };

    if (this.response.status === 'idle') {
      this.response = {
        status: 'loading',
        data: null
      };
    }

    fetchForm({
      token: this.props.token,
      reactionId: this.props.reactionId,
      url: this.props.url
    })
      .then((response) => {
        this.response = {
          status: 'success',
          data: response
        };
        this.request = {
          status: 'idle',
          data: null
        }
        this.isVisible = true;
      })
      .catch((cause) => {
        if (cause.message.includes('Cannot find form for reaction')) {
          this.response = {
            status: 'idle',
            data: null
          }

          return;
        }

        this.response = {
          status: 'error',
          data: cause.message
        };
      });
  }

  handleClick(event: MouseEvent) {
    if (this.host.isSameNode(event.target as HTMLDivElement)) {
      if (this.mode === 'modal') {
        this.onCloseHandler();
      }
    }
  }

  _getInput(field: Field, getClass) {
    switch (field.type) {
      case 'TEXT':
        return <TextInput
          id={field.id}
          key={field.id}
          onInput={(event) => this.handleInput(event, field)}
          name={field.id} label={field.label}
          hint={field.hint}
          error={this.state.errors[field.id]}
          getClass={getClass}
        />
      case 'TEXTAREA':
        return <TextareaInput
          key={field.id}
          onInput={(event) => this.handleInput(event, field)}
          name={field.id} label={field.label}
          hint={field.hint}
          error={this.state.errors[field.id]}
          getClass={getClass}
        />
      case 'CONTENT':
        return <ContentInput
          key={field.id}
          onInput={(event) => this.handleInput(event, field)}
          name={field.id} label={field.label}
          hint={field.hint}
          error={this.state.errors[field.id]}
        />
      default:
        throw new Error('Cannot match field type');
    }
  }

  render() {
    const getClass = createGetClass(this.classes);

    return this.isVisible ? (
      <Host class={clsx('root', getClass('root'))} onClick={this.handleClick.bind(this)}>
        <div class={clsx("hr-form-container", this.isVisible && "hr-form-container-visible", getClass('container'), getClass('containerVisible'))}>
          <Fetcher status={this.response.status}>
            {this.request.status === 'success' ? (
              <Success
                icon={this.request.data.icon}
                content={this.request.data.content}
                getClass={getClass}
              />
            ) : this.response.status === 'success' && (
              <Fragment>
                {this.mode === 'modal' && (
                  <div class={clsx('hr-close hr-close-top', getClass('close'), getClass('closeTop'))}>
                    <button
                      onClick={this.onCloseHandler.bind(this)}
                      type="button"
                      class={clsx('hr-close-top-button', getClass('closeTopButton'))}
                    >
                      X
                    </button>
                  </div>
                )}
                {this.response.data.content.before && (
                  <Content
                    content={this.response.data.content.before}
                    classes={clsx(getClass('content'), getClass('contentBefore'))}
                  />
                )}
                <form class={clsx("hr-form", getClass('form'))} onSubmit={this.handleSubmit.bind(this)}>
                  {this.state.errors._count > 0 && (
                    <output
                      class={clsx("hr-form-header", getClass('formHeader'))}
                      htmlFor={this.response.data.fields.map(field => field.id).join(' ')}
                      name="errors"
                    >
                      {this.state.errors._count} errors found:
                      <ul class={clsx("hr-form-errors-list", getClass('formErrorsList'))}>
                        {this.response.data.fields.map((field) => this.state.errors[field.id] ? (
                          <li class={clsx("hr-form-error-item", getClass('formErrorItem'))}>
                            {this.state.errors[field.id]}
                          </li>
                        ) : null)}
                      </ul>
                    </output>
                  )}
                  {this.response.data.fields.map((field) => {
                    return this._getInput(field, getClass);
                  })}
                  <div class={clsx("hr-footer", getClass('footer'))}>
                    {this.request.status === 'idle' && (
                      <button type="submit" class={clsx("hr-submit hr-submit-idle", getClass('submit'), getClass('submitIdle'))}>
                        Send
                      </button>
                    )}
                    {this.request.status === 'loading' && (
                      <button type="submit" class={clsx("hr-submit hr-submit-loading", getClass('submit'), getClass('submitIdle'))} disabled>
                        Saving...
                      </button>
                    )}
                  </div>

                </form>
                {this.response.data.content.after && (
                  <Content
                    content={this.response.data.content.after}
                    classes={clsx(getClass('content'), getClass('contentAfter'))}
                  />
                )}
                {this.mode === 'modal' && (
                  <div class={clsx('hr-close hr-close-bottom', getClass('close'), getClass('closeBottom'))}>
                    <button
                      onClick={this.onCloseHandler.bind(this)}
                      type="button"
                      class={clsx('hr-close-bottom-button', getClass('closeBottomButton'))}
                    >
                      Close
                    </button>
                  </div>
                )}
              </Fragment>
            )}
          </Fetcher>
        </div>
      </Host>
    ) : null;
  }

}
